import type { IntlShape } from 'react-intl'

/**
 * Formats time remaining until a date using calendar months and days.
 * Accounts for actual month lengths (28/30/31 days) and localises the
 * plural forms via react-intl.
 *
 * Examples (English):
 *   - "3 months and 9 days left"
 *   - "1 month left"
 *   - "15 days left"
 *   - undefined if already expired
 */
export function formatTimeRemaining(expiresAt: string, intl: IntlShape): string | undefined {
  const now = new Date()
  const expiry = new Date(expiresAt)

  if (expiry <= now) return undefined

  let months = 0
  const cursor = new Date(now)

  // Count full calendar months
  while (true) {
    const next = new Date(cursor)
    next.setMonth(next.getMonth() + 1)
    if (next > expiry) break
    cursor.setMonth(cursor.getMonth() + 1)
    months++
  }

  // Remaining days after full months
  const diffMs = expiry.getTime() - cursor.getTime()
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24))

  if (months === 0) {
    return intl.formatMessage(
      {
        id: 'qai4g4paVC8cREHYYaRT',
        defaultMessage: '{days, plural, one {# day} other {# days}} left',
        description: 'Time remaining until expiry when less than one full month, e.g. "15 days left"',
      },
      { days },
    )
  }

  if (days === 0) {
    return intl.formatMessage(
      {
        id: 'ZzSJB_A3tRDcmVqgfkWU',
        defaultMessage: '{months, plural, one {# month} other {# months}} left',
        description: 'Time remaining until expiry when exactly a whole number of months, e.g. "3 months left"',
      },
      { months },
    )
  }

  return intl.formatMessage(
    {
      id: 'xg0GEbRWAy0CQ49OhWuX',
      defaultMessage: '{months, plural, one {# month} other {# months}} and {days, plural, one {# day} other {# days}} left',
      description: 'Time remaining until expiry with both months and days, e.g. "3 months and 9 days left"',
    },
    { months, days },
  )
}
